import React, { useState, useEffect, useCallback } from "react";
import { FaCheckCircle, FaTrash, FaCheck, FaUserAlt, FaCalendarAlt, FaPagelines, FaClock, FaCalendarPlus, FaTree, FaMapMarkerAlt, FaSearch, FaTimes } from "react-icons/fa";
import api from "../../axios/api";
import { ToastContainer, toast } from 'react-toastify';
import "../../assets/NewFiles/Css/Dashboard.css";
import Select from 'react-select';
import Loader from "../common/Loader";
import Button from "../common/styled components/Button";
import { Box, Pagination } from "@mui/material";

const VerifyPosts = () => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("latest");
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 9
  });

  const rejectionOptions = [
    { value: "not_tree", label: "Not Tree" },
    { value: "blurry_image", label: "Blurry Image" },
    { value: "duplicate_image", label: "Duplicate Image" },
    { value: "dead_tree", label: "Dead Tree" },
    { value: "need_to_contact", label: "Need to contact" },
  ];

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/admin/getPosts", {
        params: {
          page: pagination.currentPage,
          pageSize: pagination.pageSize,
          search: searchTerm,
          filter: filter,
          sortOrder: sortOrder
        }
      });
      
      setPosts(response.data.images);
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.totalPages,
        totalItems: response.data.totalItems
      }));
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to fetch posts");
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, searchTerm, filter, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPosts();
    }, 400); // Debounce searches

    return () => clearTimeout(timer);
  }, [fetchPosts]);

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleFilter = (e) => setFilter(e.target.value);
  
  const handleSortOrder = (e) => {
    setSortOrder(e.target.value);
    setPagination(prev => ({...prev, currentPage: 1})); // Reset to first page when changing sort
  };

  const handleVerify = async (id) => {
    setIsVerifying(true);
    try {
      await api.patch(`/api/admin/posts/${id}/verify`);
      toast.success("Verified");
      fetchPosts();
    } catch (error) {
      console.error("Error verifying post:", error);
      toast.error("Failed to verify post");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDelete = async (id) => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/admin/posts/${id}`);
      toast.error("Deleted");
      fetchPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReject = async (id, reason) => {
    setIsRejecting(true);
    try {
      await api.post(`/api/admin/posts/reject/${id}`, { Reason: reason.value });
      toast.error("Rejected");
      fetchPosts();
    } catch (error) {
      console.error("Error rejecting post:", error);
      toast.error("Failed to reject post");
    } finally {
      setIsRejecting(false);
    }
  };

  const handleSelectPost = (id) => {
    setSelectedPosts((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((postId) => postId !== id)
        : [...prevSelected, id]
    );
  };

  const handleVerifySelected = async () => {
    setIsVerifying(true);
    try {
      await Promise.all(
        selectedPosts.map((id) => api.patch(`/api/admin/posts/${id}/verify`))
      );
      setSelectedPosts([]);
      toast.success("Selected posts verified");
      fetchPosts();
    } catch (error) {
      console.error("Error verifying selected posts:", error);
      toast.error("Failed to verify selected posts");
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <ToastContainer />
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-7 bg-emerald-500 rounded-full flex-shrink-0" />
          <h1 className="text-2xl sm:text-[1.75rem] font-bold text-[#002F43] tracking-tight m-0 leading-tight">
            Verify Trees
          </h1>
        </div>

        {/* Modern Search & Filter Box */}
        <div className="bg-white p-3.5 sm:p-4 rounded-xl shadow-xs border border-gray-200/80 mb-5">
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
            {/* Search Input Bar (Plant Name) */}
            <div className="relative flex-1 w-full sm:w-auto">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-600 text-sm pointer-events-none" />
              <input
                type="text"
                placeholder="Search by plant name..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full !pl-10 !pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
                style={{ paddingLeft: '40px', paddingRight: '40px' }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs p-1 cursor-pointer"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Filter by Planter Name */}
            <div className="w-full sm:w-56 flex-shrink-0">
              <input
                type="text"
                placeholder="Filter by Planter Name..."
                value={filter}
                onChange={handleFilter}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
              />
            </div>

            {/* Sort Select */}
            <div className="w-full sm:w-36 flex-shrink-0">
              <select
                value={sortOrder}
                onChange={handleSortOrder}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none cursor-pointer"
              >
                <option value="latest">Sort: Latest</option>
                <option value="oldest">Sort: Oldest</option>
              </select>
            </div>
          </div>
        </div>
        
        <div style={{ marginTop: '15px', marginBottom: '15px' }}>
          <Button
            onClick={handleVerifySelected}
            disabled={selectedPosts.length === 0 || isVerifying || isLoading}
          >
            <FaCheck />
            <span className="ml-2">Verify Selected ({selectedPosts.length})</span>
          </Button>
        </div>

        <div className="posts-grid">
          {posts.map((post) => (
            <div
              key={post.id}
              className={`post-card ${post.isVerified === 1 ? "verified" : ""}`}
            >
              <input
                type="checkbox"
                checked={selectedPosts.includes(post.id)}
                onChange={() => handleSelectPost(post.id)}
                className="select-checkbox"
                disabled={isLoading}
              />
              <img
                src={post.treeImageUrl}
                alt={post.imageName}
                className="post-image"
              />
              <div className="w-full text-left p-2.5">
                <h3 className="text-base font-bold text-gray-800 m-0 mb-2 flex items-center gap-1.5" title="Tree Name">
                  {post.isVerified === 1 && <FaCheckCircle className="text-emerald-600 text-sm flex-shrink-0" />}
                  <span className="truncate">{post.treeName}</span>
                </h3>

                <div className="space-y-1.5 text-xs text-gray-700 text-left">
                  <div className="flex items-start gap-2 text-left" title="Tree Id">
                    <FaTree color="#002D62" size={13} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1 text-left">
                      <span className="font-semibold text-gray-900">Tree ID : </span>
                      <span className="text-gray-700">{post.tree_id}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-left" title="Uploaded By">
                    <FaUserAlt color="#002D62" size={13} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1 text-left">
                      <span className="font-semibold text-gray-900">Uploaded by : </span>
                      <span className="text-gray-700">{post.full_name}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-left" title="Uploaded On">
                    <FaCalendarPlus color="#002D62" size={13} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1 text-left">
                      <span className="font-semibold text-gray-900">Uploaded on : </span>
                      <span className="text-gray-700">{new Date(post.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-left" title="Planted On">
                    <FaCalendarAlt color="#002D62" size={13} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1 text-left">
                      <span className="font-semibold text-gray-900">Planted on : </span>
                      <span className="text-gray-700">{new Date(post.plantedOn).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-left" title="Species">
                    <FaPagelines color="#002D62" size={13} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1 text-left">
                      <span className="font-semibold text-gray-900">Species : </span>
                      <span className="text-gray-700">{post.speciesLabel || post.tree_species}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-left" title="Age">
                    <FaClock color="#002D62" size={13} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1 text-left">
                      <span className="font-semibold text-gray-900">Age : </span>
                      <span className="text-gray-700">{post.age} year(s)</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-left" title="Location">
                    <FaMapMarkerAlt color="#002D62" size={13} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1 text-left">
                      <span className="font-semibold text-gray-900">Location : </span>
                      <span className="text-gray-700 break-words">{post.address}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="post-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                <button
                  onClick={() => handleVerify(post.id)}
                  className={`verify-button ${post.isVerified === 1 ? "verified" : ""}`}
                  style={{
                    fontSize: '12px',
                    padding: '5px 10px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: 'none',
                    color: 'white',
                    backgroundColor: post.isVerified === 1 ? '#28a745' : '#ccc',
                    cursor: post.isVerified === 1 ? 'default' : 'pointer',
                  }}
                  disabled={post.isVerified === 1 || isVerifying || isLoading}
                >
                  <FaCheckCircle style={{ marginRight: '5px' }} /> {post.isVerified === 1 ? "Verified" : "Verify"}
                </button>

                <button
                  onClick={() => handleDelete(post.id)}
                  className="delete-button"
                  style={{
                    fontSize: '12px',
                    padding: '5px 10px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: 'none',
                    color: 'white',
                    backgroundColor: '#dc3545',
                    cursor: 'pointer',
                  }}
                  disabled={isDeleting || isLoading}
                >
                  <FaTrash style={{ marginRight: '5px' }} /> Delete
                </button>

                <div style={{ width: '120px' }}>
                  <Select
                    options={rejectionOptions}
                    onChange={(selectedOption) => handleReject(post.id, selectedOption)}
                    placeholder="Reject Post"
                    className="reject-select"
                    classNamePrefix="reject-select"
                    isDisabled={isRejecting || isLoading}
                    menuPlacement="top"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        fontSize: '12px',
                        padding: '0',
                        height: '32px',
                        minHeight: '32px',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        fontSize: '12px',
                        zIndex: 20,
                      }),
                      indicatorsContainer: (provided) => ({
                        ...provided,
                        height: '32px',
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Pagination Controls */}
        {posts.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              className="pagination-container"
              showFirstButton
              showLastButton
              disabled={isLoading}
            />
          </Box>
        )}
        
        {posts.length === 0 && !isLoading && (
          <div style={{ textAlign: 'center', padding: '30px', marginTop: '20px' }}>
            <p>No posts found matching your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyPosts;